import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../design/Button';
import TextField from '../design/TextField';
import { addStudent } from '../utils/schoolFirestore';
import type { SchoolClass, Student } from '../types';

interface AddStudentFormProps {
  schoolClass: SchoolClass;
  existing: Student[];
  onAdded: (student: Student) => void;
}

const AddStudentForm: React.FC<AddStudentFormProps> = ({ schoolClass, existing, onAdded }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [lastAdded, setLastAdded] = useState<Student | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    setSaving(true);
    setError(false);
    try {
      const student = await addStudent(schoolClass, firstName, lastName, existing);
      setLastAdded(student);
      setFirstName('');
      setLastName('');
      onAdded(student);
    } catch (err) {
      console.error('[school] Failed to add student', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          label={t('school_first_name')}
          value={firstName}
          onChange={setFirstName}
          maxLength={40}
        />
        <TextField
          label={t('school_last_name')}
          value={lastName}
          onChange={setLastName}
          maxLength={40}
        />
      </div>
      <Button
        type="submit"
        icon="plus"
        loading={saving}
        disabled={!firstName.trim() || !lastName.trim()}
      >
        {t('school_add_student')}
      </Button>
      {error && (
        <p className="text-sm text-school-danger" role="alert">
          {t('school_error_add_student')}
        </p>
      )}
      {lastAdded && (
        <p
          className="rounded-xl bg-school-success-soft p-3 text-sm text-school-success"
          aria-live="polite"
        >
          {t('school_student_added', {
            name: `${lastAdded.firstName} ${lastAdded.lastName}`,
            code: lastAdded.loginCode,
          })}
        </p>
      )}
    </form>
  );
};

export default AddStudentForm;
